// @flow
import { takeEvery, put, call } from 'redux-saga/effects';

import axios from 'axios';
import snakeCaseKeys from 'snakecase-keys';
import {
  API_GET_ARRAY,
  API_GET,
  API_CREATE,
  API_UPDATE,
  API_DELETE
} from '@redux/components/ApiRequest/constants';
import type {
  Action,
  CreateAction,
  GetAction,
  UpdateAction,
  DeleteAction,
  GetArrayResponse
} from '@redux/components/ApiRequest/types';
import { getHeaders } from '@services/httpHeaders';
import { handleApiErrors } from '@lib';

import type { NextCtx } from '@globalTypes';

export function* apiGetArrayFlow(
  nextCtx: NextCtx,
  action: GetAction
): Generator<Function, void, void> {
  const {
    url, successAction, errorAction, options
  }: GetAction = action;

  try {
    const response: ?GetArrayResponse = yield call(axios.get, `${url}/`, {
      headers: getHeaders(nextCtx),
      ...(options || {})
    });

    if (!response) throw new Error();

    const data: ?Array<{}> = yield call(snakeCaseKeys, response.data.data);

    const { included } = response.data;
    const { headers } = response;
    const { meta } = response.data;

    yield put(successAction(data || [], included || [], headers || {}, meta || {}));
  } catch (error) {
    yield call(handleApiErrors, error, action);
    yield put(errorAction(error));
  }
}

export function* apiGetFlow(nextCtx: NextCtx, action: GetAction): Generator<Function, void, void> {
  const { url, successAction, errorAction }: GetAction = action;

  try {
    const response: ?GetArrayResponse = yield call(axios.get, `${url}/`, {
      headers: getHeaders(nextCtx)
    });
    if (!response) throw new Error();

    const data: ?{} = yield call(snakeCaseKeys, response.data.data);

    const { included } = response.data;

    yield put(successAction(data || {}, included || []));
  } catch (error) {
    yield call(handleApiErrors, error, action);
    yield put(errorAction(error));
  }
}

export function* apiCreateFlow(
  nextCtx: NextCtx,
  action: CreateAction
): Generator<Function, void, void> {
  const {
    url, body, successAction, errorAction
  }: CreateAction = action;

  try {
    yield call(axios.post, `${url}/`, body, {
      headers: getHeaders(nextCtx)
    });
    yield put(successAction());
  } catch (error) {
    const errorData: ?{} = (error.response || { data: {} }).data.errors;

    yield call(handleApiErrors, error, action);
    yield put(errorAction(errorData || {}));
  }
}

export function* apiUpdateFlow(
  nextCtx: NextCtx,
  action: UpdateAction
): Generator<Function, void, void> {
  const {
    url, body, successAction, errorAction, id
  }: UpdateAction = action;

  try {
    yield call(axios.patch, `${url}/`, body, {
      headers: getHeaders(nextCtx)
    });
    yield put(successAction(id));
  } catch (error) {
    const errorData: ?{} = (error.response || { data: {} }).data.errors;

    yield call(handleApiErrors, error, action);
    yield put(errorAction(id, errorData || {}));
  }
}

export function* apiDeleteFlow(
  nextCtx: NextCtx,
  action: DeleteAction
): Generator<Function, void, void> {
  const {
    url, id, successAction, errorAction
  }: DeleteAction = action;

  try {
    yield call(axios.delete, `${url}/`, {
      headers: getHeaders(nextCtx)
    });
    yield put(successAction(id));
  } catch (error) {
    yield call(handleApiErrors, error, action);
    if (errorAction) yield put(errorAction(id, error));
  }
}

export function* apiRequestFlow(nextCtx: NextCtx, action: Action): Generator<Function, void, void> {
  switch (action.type) {
    case API_GET_ARRAY:
      yield call(apiGetArrayFlow, nextCtx, action);
      break;
    case API_GET:
      yield call(apiGetFlow, nextCtx, action);
      break;
    case API_CREATE:
      yield call(apiCreateFlow, nextCtx, action);
      break;
    case API_UPDATE:
      yield call(apiUpdateFlow, nextCtx, action);
      break;
    case API_DELETE:
      yield call(apiDeleteFlow, nextCtx, action);
      break;
    default:
      break;
  }
}

function* apiRequestWatcher(nextCtx: NextCtx): Generator<Function, void, Action> {
  yield takeEvery(
    [API_GET_ARRAY, API_GET, API_CREATE, API_UPDATE, API_DELETE],
    apiRequestFlow,
    nextCtx
  );
}

export default apiRequestWatcher;
